import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';

const FilterSelect = ({
  label,
  value,
  options,
  onChange,
  className = '',
  disabled = false,
  menuPlacement = 'auto',
  searchable = true,
  usePortal = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [resolvedPlacement, setResolvedPlacement] = useState(menuPlacement === 'top' ? 'top' : 'bottom');
  const [menuMaxHeight, setMenuMaxHeight] = useState(256);
  const [menuPosition, setMenuPosition] = useState({ left: 0, top: 0, width: 0 });
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((option) => String(option.value) === String(value)) || options[0],
    [options, value]
  );
  const visibleOptions = useMemo(() => {
    if (!searchable) return options;
    const normalizedQuery = String(query || '').trim().toLowerCase();
    if (!normalizedQuery) return options;
    return options.filter((option) => String(option?.label || '').toLowerCase().includes(normalizedQuery));
  }, [options, query, searchable]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current?.contains(event.target) || menuRef.current?.contains(event.target)) {
        return;
      }
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || disabled || !usePortal) return undefined;

    const resolvePlacement = () => {
      const buttonRect = buttonRef.current?.getBoundingClientRect();
      if (!buttonRect) return;

      const viewportPadding = 12;
      const menuGap = 12;
      const preferredMaxHeight = 256;
      const viewportWidth = window.innerWidth;

      const availableAbove = Math.max(0, buttonRect.top - viewportPadding);
      const availableBelow = Math.max(0, window.innerHeight - buttonRect.bottom - viewportPadding);

      let nextPlacement = 'bottom';
      if (menuPlacement === 'top' || menuPlacement === 'bottom') {
        nextPlacement = menuPlacement;
      } else {
        // For auto placement, prefer the side with more visible space.
        // This avoids "cut from bottom" behavior across pages and containers.
        nextPlacement = availableAbove > availableBelow ? 'top' : 'bottom';
      }

      const maxHeightByPlacement = nextPlacement === 'top' ? availableAbove : availableBelow;
      const boundedAvailableHeight = Number.isFinite(maxHeightByPlacement)
        ? Math.max(0, maxHeightByPlacement)
        : preferredMaxHeight;
      const nextMaxHeight = boundedAvailableHeight > 0
        ? Math.max(120, Math.min(preferredMaxHeight, boundedAvailableHeight))
        : 120;

      const maxMenuWidth = Math.max(220, viewportWidth - viewportPadding * 2);
      const nextWidth = Math.min(buttonRect.width, maxMenuWidth);
      const nextLeft = Math.min(
        Math.max(viewportPadding, buttonRect.left),
        viewportWidth - nextWidth - viewportPadding
      );
      const nextTop = nextPlacement === 'top'
        ? buttonRect.top - menuGap
        : buttonRect.bottom + menuGap;

      setResolvedPlacement(nextPlacement);
      setMenuMaxHeight(nextMaxHeight);
      setMenuPosition({
        left: nextLeft,
        top: nextTop,
        width: nextWidth,
      });
    };

    resolvePlacement();
    window.addEventListener('resize', resolvePlacement);
    window.addEventListener('scroll', resolvePlacement, true);

    return () => {
      window.removeEventListener('resize', resolvePlacement);
      window.removeEventListener('scroll', resolvePlacement, true);
    };
  }, [disabled, isOpen, menuPlacement, usePortal]);

  const handleSelect = (nextValue) => {
    if (disabled) return;
    const selectedOption = options.find((option) => String(option.value) === String(nextValue));
    if (selectedOption?.disabled) return;
    onChange(nextValue);
    setIsOpen(false);
    setQuery('');
  };

  const menuNode = isOpen && !disabled ? (
    <div
      ref={menuRef}
      className={`${usePortal ? 'fixed z-[400]' : 'absolute z-30 mt-1 w-full'} overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_28px_48px_-24px_rgba(15,23,42,0.5)]`}
      style={usePortal ? {
        left: `${menuPosition.left}px`,
        top: `${menuPosition.top}px`,
        width: `${menuPosition.width}px`,
        transform: resolvedPlacement === 'top' ? 'translateY(-100%)' : 'none',
      } : undefined}
    >
      {searchable ? (
        <div className="border-b border-slate-200 px-2 py-2">
          <input
            type="text"
            className="input w-full rounded-md border-slate-200 px-2.5 py-1.5 text-sm"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search..."
          />
        </div>
      ) : null}
      <div className="overflow-y-auto py-2" style={{ maxHeight: `${menuMaxHeight}px` }}>
        {visibleOptions.map((option) => {
          const isSelected = String(option.value) === String(value);
          const isOptionDisabled = Boolean(option.disabled);
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              disabled={isOptionDisabled}
              className={`flex w-full items-center justify-between px-4 py-3.5 text-left text-sm transition-colors ${
                isSelected
                  ? 'bg-primary-50 font-semibold text-primary-700'
                  : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
              } ${isOptionDisabled ? 'cursor-not-allowed opacity-45 hover:bg-transparent hover:text-slate-700' : ''}`}
            >
              <span className="truncate">{option.label}</span>
              {isSelected ? <Check className="h-4 w-4 text-primary-600" /> : null}
            </button>
          );
        })}
        {visibleOptions.length === 0 ? (
          <div className="px-4 py-3 text-sm text-slate-500">No options found</div>
        ) : null}
      </div>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="mb-1 block text-sm font-medium text-slate-700">{label}</label>
      )}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen((current) => !current)}
          disabled={disabled}
          className={`flex items-center justify-between gap-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm transition-colors text-left ${
            isOpen ? 'border-primary-500 ring-2 ring-primary-500 bg-primary-50' : 'hover:border-slate-300'
          } ${
            disabled ? 'cursor-not-allowed bg-slate-50 text-slate-400 border-slate-200' : ''
          }`}
        >
          <span className={`truncate ${disabled ? 'text-slate-400' : 'text-slate-900'}`}>{selectedOption?.label || ''}</span>
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 ${
              isOpen ? 'rotate-180 text-primary-500' : ''
            }`}
          />
        </button>
      </div>
      {menuNode
        ? (usePortal && typeof document !== 'undefined' ? createPortal(menuNode, document.body) : menuNode)
        : null}
    </div>
  );
};

export default FilterSelect;
